import type React from "react";
import {useState} from "react";
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from "@mui/material";
import {PATH_SEP} from "~/constants";

type TaxonomyMergeCategoriesDialogProps = {
    /** Names of the categories to merge, in tree order */
    labels: string[];
    /** Names from L1 down to their parent; empty when they are L1s. */
    parentPath: string[];
    /** Names of the parent's other categories: the merged name must differ (case-insensitive). */
    otherSiblingNames: string[];
    /** Sub-categories of all of them together: they move to the merged category. */
    subCategoryCount: number;
    isSaving: boolean;
    onCancel: () => void;
    onConfirm: (name: string, description: string) => void;
};

/**
 * Confirms a merge and names the new category (required, unique under the parent) with an optional
 * description, as the current builder's merge dialog does. The name starts as the first category's.
 * Mount it fresh for each merge.
 */
export const TaxonomyMergeCategoriesDialog: React.FC<TaxonomyMergeCategoriesDialogProps> = ({
                                                                                                labels,
                                                                                                parentPath,
                                                                                                otherSiblingNames,
                                                                                                subCategoryCount,
                                                                                                isSaving,
                                                                                                onCancel,
                                                                                                onConfirm,
                                                                                            }) => {
    const [name, setName] = useState(labels[0] ?? '');
    const [description, setDescription] = useState('');
    const trimmed = name.trim();
    const duplicate = trimmed !== '' && otherSiblingNames.some(s => s.toLowerCase() === trimmed.toLowerCase());
    const canConfirm = trimmed !== '' && !duplicate && !isSaving;
    const confirm = () => {
        if (canConfirm) onConfirm(trimmed, description.trim());
    };

    return (
        <Dialog open onClose={isSaving ? undefined : onCancel} maxWidth="xs" fullWidth>
            <DialogTitle>Merge categories</DialogTitle>
            <DialogContent>
                <Stack gap={2} sx={{pt: 0.5}}>
                    <Stack gap={0.5}>
                        <Typography variant="caption" color="text.secondary">Parent path</Typography>
                        <Typography variant="body2" fontWeight={parentPath.length ? 600 : 400} color={parentPath.length ? 'text.primary' : 'text.secondary'}>
                            {parentPath.length ? parentPath.join(PATH_SEP) : 'L1'}
                        </Typography>
                    </Stack>
                    <DialogContentText>
                        {labels.map((label, i) => (
                            <span key={label}>
                                {i > 0 && (i === labels.length - 1 ? ' and ' : ', ')}
                                <Box component="strong" sx={{color: 'text.primary'}}>{label}</Box>
                            </span>
                        ))}
                        {' '}are replaced by one new category with their spend
                        {subCategoryCount > 0
                            ? <> and their {subCategoryCount} {subCategoryCount === 1 ? 'sub-category' : 'sub-categories'}.</>
                            : '.'}
                    </DialogContentText>
                    <TextField
                        label="Name of the merged category"
                        value={name}
                        onChange={e => setName(e.target.value)}
                        onKeyDown={e => {
                            if (e.key === 'Enter') confirm();
                        }}
                        onFocus={e => e.target.select()}
                        error={duplicate}
                        helperText={duplicate ? `A category with this name already exists under ${parentPath.at(-1) ?? 'L1'}.` : undefined}
                        autoFocus
                        fullWidth
                        size="small"
                    />
                    <TextField
                        label="Description"
                        placeholder="What belongs in this category (optional)"
                        value={description}
                        onChange={e => setDescription(e.target.value)}
                        multiline
                        minRows={3}
                        fullWidth
                        size="small"
                    />
                </Stack>
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} disabled={isSaving}>Cancel</Button>
                <Button variant="contained" onClick={confirm} loading={isSaving} disabled={!canConfirm}>
                    Merge
                </Button>
            </DialogActions>
        </Dialog>
    );
};
